import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SiteSearch from '../Common/SiteSearch';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Menu as MenuIcon, X as XIcon, ChevronDown, ChevronRight,
    Bell, Globe, GraduationCap, BookOpen, Building2,
    Users, Shield, FileText, Youtube,
    FileCheck, UserCog, FolderOpen,
    Camera, Microscope, ExternalLink,
    ArrowRight, Layers
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackNavigation, trackExternalClick, trackButtonClick } from '../../utils/analytics';
import { useDuyurular } from '../../hooks/useSupabaseData';
import { fetchAkademikFaaliyet } from '../../utils/akademikFaaliyetCache';
import './Navbar.css';

const getNavLinks = (t, currentLang) => {
    const prefix = currentLang === 'en' ? '/en' : '';
    return [
        { title: t('nav.home'), href: prefix + "/" },
        { title: t('nav.institutional'), sublinks: [
            { title: t('nav.history'), href: prefix + "/kurumsal/tarihce" },
            { title: t('nav.missionVision'), href: prefix + "/kurumsal/misyon-vizyon" },
            { title: t('nav.fedek'), href: prefix + "/kurumsal/fedek" },
            { title: t('nav.strategicPlan'), href: prefix + "/kurumsal/stratejik-plan" },
            { title: t('nav.formerDeans'), href: prefix + "/kurumsal/eski-dekanlar" },
        ]},
        { title: t('nav.management'), sublinks: [
            { title: t('nav.dean'), href: prefix + "/yonetim/dekan" },
            { title: t('nav.viceDeans'), href: prefix + "/yonetim/dekan-yardimcilari" },
            { title: t('nav.facultySecretary'), href: prefix + "/yonetim/fakulte-sekreteri" },
            { title: t('nav.facultyBoard'), href: prefix + "/yonetim/fakulte-kurulu" },
            { title: t('nav.managementBoard'), href: prefix + "/yonetim/yonetim-kurulu" },
            { title: t('nav.unitActivities'), href: prefix + "/birim-faaliyetleri" },
        ]},
        { title: t('nav.boardsCommissions'), sublinks: [
            { title: t('nav.qualityStrategy'), href: prefix + "/kurul-komisyonlar/kalite-strateji" },
            { title: t('nav.academicQuality'), href: prefix + "/kurul-komisyonlar/akademik-birim-kalite" },
            { title: t('nav.scholarship'), href: prefix + "/kurul-komisyonlar/burs-komisyonu" },
            { title: t('nav.promotionCeremony'), href: prefix + "/kurul-komisyonlar/tanitim-toren" },
            { title: t('nav.publication'), href: prefix + "/kurul-komisyonlar/yayin" },
            { title: t('nav.nonFormalLearning'), href: prefix + "/kurul-komisyonlar/program-disi-ogrenme" },
            { title: t('nav.advisoryBoard'), href: prefix + "/kurul-komisyonlar/birim-danisma-kurulu" },
        ]},
        { title: t('nav.facultyDecisions'), sublinks: [
            { title: t('nav.facultyBoardDecisions'), href: prefix + "/kararlar/fakulte-kurulu" },
            { title: t('nav.managementBoardDecisions'), href: prefix + "/kararlar/fakulte-yonetim" },
        ]},
        { title: t('nav.facultyPersonnel'), sublinks: [
            { title: t('nav.faculty'), href: prefix + "/fakulte-personeli/ogretim-uyeleri" },
            { title: t('nav.administrativeStaff'), href: prefix + "/fakulte-personeli/idari-personel" },
            { title: t('nav.retiredFaculty'), href: prefix + "/fakulte-personeli/emekli-ogretim-elemanlari" },
        ]},
        { title: t('nav.research'), sublinks: [
            { title: t('nav.labs'), href: prefix + "/arastirma/laboratuvarlar" },
            { title: t('nav.books'), href: prefix + "/arastirma/kitaplar-editorlukler" },
            { title: t('nav.journals'), href: prefix + "/arastirma/dergiler" },
            { title: t('nav.museums'), href: prefix + "/arastirma/muzeler" },
            { title: t('nav.ongoingProjects'), href: prefix + "/arastirma/devam-eden-projelerimiz" },
            { title: t('nav.academicActivity'), href: prefix + "/akademik-faaliyet" },
        ]},
        { title: t('nav.student'), sublinks: [
            { title: t('nav.studentGuide'), href: "https://fs.hacettepe.edu.tr/fenyeni/dosyalar/ogrencikilavuzu/ogrencikilavuzu.pdf", external: true },
            { title: t('nav.usefulDocuments'), href: prefix + "/belgeler/ogrenci" },
            { title: t('nav.internship'), href: prefix + "/staj" },
            { title: t('nav.studentClubs'), href: prefix + "/ogrenci-topluluklari" },
            { title: t('nav.erasmus'), href: prefix + "/programlar/erasmus" },
            { title: t('nav.internationalStudents'), href: currentLang === 'en' ? "/en/student/international-students" : "/ogrenci/uluslararasi-ogrenciler" },
        ]},
        { title: t('nav.departments'), sublinks: [
            { title: t('nav.actuarialScience'), href: "https://aktuerya.hacettepe.edu.tr", external: true },
            { title: t('nav.biology'), href: "https://biology.hacettepe.edu.tr", external: true },
            { title: t('nav.chemistry'), href: "https://chem.hacettepe.edu.tr", external: true },
            { title: t('nav.mathematics'), href: "https://mat.hacettepe.edu.tr", external: true },
            { title: t('nav.statistics'), href: "https://stat.hacettepe.edu.tr", external: true },
        ]},
        { title: t('nav.programs'), sublinks: [
            { title: t('nav.undergraduate'), href: prefix + "/programlar/lisans" },
            { title: t('nav.masters'), href: prefix + "/programlar/yuksek-lisans" },
            { title: t('nav.doctorate'), href: prefix + "/programlar/doktora" },
            { title: t('nav.minor'), href: prefix + "/programlar/yandal" },
            { title: t('nav.doubleMajor'), href: prefix + "/programlar/cift-anadal" },
            { title: t('nav.erasmus'), href: prefix + "/programlar/erasmus" },
        ]},
        { title: t('nav.documents'), sublinks: [
            { title: t('nav.lawsRegulations'), href: prefix + "/belgeler/kanun-yonetmelikler" },
            { title: t('nav.academicAdministrative'), href: prefix + "/belgeler/akademik-idari-personel" },
            { title: t('nav.student'), href: prefix + "/belgeler/ogrenci" },
        ]},
        { title: t('nav.accessibleLife'), href: prefix + "/engelsiz-yasam" },
        { title: t('nav.socialResponsibility'), href: prefix + "/sosyal-sorumluluk-projeleri" },
        { title: t('nav.gallery'), sublinks: [
            { title: t('nav.photoGallery'), href: prefix + "/albums" },
            { title: t('nav.videoGallery'), href: prefix + "/videogaleri" },
        ]},
        { title: t('nav.contact'), href: prefix + "/iletisim" },
    ];
};

const getDepartmentPromotions = (t, currentLang) => {
    const prefix = currentLang === 'en' ? '/en' : '';
    return [
        { title: t('navbar.facultyPromotion'), href: "https://www.youtube.com/watch?v=f7tTbfwei9Q", icon: "Youtube", external: true },
        { title: t('navbar.biologyPromotion'), href: "https://www.youtube.com/watch?v=6OqEm9H9knk", icon: "Youtube", external: true },
        { title: t('navbar.chemistryPromotion'), href: "https://www.youtube.com/watch?v=CgduFVGf6LI", icon: "Youtube", external: true },
        { title: t('navbar.actuarialPromotion'), href: "https://aktuerya.hacettepe.edu.tr/duyurular/tanitim25.pdf", icon: "FileText", external: true },
        { title: t('navbar.mathematicsPromotion'), href: "https://mat.hacettepe.edu.tr/duyurular/tanitim_sunum.pdf", icon: "FileText", external: true },
        { title: t('navbar.statisticsPromotion'), href: "https://www.youtube.com/watch?v=rMbJeSIByPA", icon: "Youtube", external: true },
        { title: "divider", isDivider: true },
        { title: t('navbar.virtualTours'), isHeader: true },
        { title: t('navbar.chemistryVirtualTour'), href: prefix + "/bolumler/kimya-bolumu-sanal-tur", icon: "Building2", external: false },
    ];
};

/* ─── ANA MEGA MENÜ ─── */
const MegaMenu = ({ isOpen, setIsOpen, changeLanguage, currentLang }) => {
    const { t, i18n } = useTranslation();
    const { duyurular } = useDuyurular();
    const [activeIdx, setActiveIdx] = useState(null);
    const [yayinSayisi, setYayinSayisi] = useState(null);
    const [yayinHata, setYayinHata] = useState(false);

    const close = useCallback(() => setIsOpen(false), [setIsOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setActiveIdx(null);
            fetchAkademikFaaliyet()
                .then(data => setYayinSayisi(data.makaleler.length))
                .catch(() => setYayinHata(true));
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [close]);

    const navLinks = useMemo(() => getNavLinks(t, i18n.language), [t, i18n.language]);
    const prefix = i18n.language === 'en' ? '/en' : '';

    const sections = useMemo(() => {
        const find = (key) => navLinks.find(l => l.title === t(key));
        return [
            { label: t('nav.institutional'),    icon: Building2,     color: '#E11D48', links: find('nav.institutional')?.sublinks    || [] },
            { label: t('nav.management'),        icon: Users,         color: '#2563EB', links: find('nav.management')?.sublinks        || [] },
            { label: t('nav.boardsCommissions'), icon: Shield,        color: '#7C3AED', links: find('nav.boardsCommissions')?.sublinks || [] },
            { label: t('nav.facultyDecisions'),  icon: FileCheck,     color: '#EA580C', links: find('nav.facultyDecisions')?.sublinks  || [] },
            { label: t('nav.facultyPersonnel'),  icon: UserCog,       color: '#0891B2', links: find('nav.facultyPersonnel')?.sublinks  || [] },
            { label: t('nav.research'),          icon: Microscope,    color: '#059669', links: find('nav.research')?.sublinks          || [] },
            { label: t('nav.student'),           icon: GraduationCap, color: '#D97706', links: find('nav.student')?.sublinks           || [] },
            { label: t('nav.programs'),          icon: BookOpen,      color: '#3B82F6', links: find('nav.programs')?.sublinks          || [] },
            { label: t('nav.documents'),         icon: FolderOpen,    color: '#64748B', links: find('nav.documents')?.sublinks         || [] },
            { label: t('nav.gallery'),           icon: Camera,        color: '#DB2777', links: find('nav.gallery')?.sublinks           || [] },
        ];
    }, [t, navLinks]);

    const singleLinks = useMemo(() => {
        const find = (key) => navLinks.find(l => l.title === t(key));
        return [find('nav.accessibleLife'), find('nav.socialResponsibility'), find('nav.contact')]
            .filter(l => l && !l.sublinks);
    }, [t, navLinks]);

    const latestAnnouncement = useMemo(() =>
        duyurular.length > 0
            ? [...duyurular].sort((a, b) => new Date(b.date) - new Date(a.date))[0]
            : null,
    [duyurular]);

    const deptCards = useMemo(() => [
        { title: 'Aktüerya Bilimleri', href: 'https://aktuerya.hacettepe.edu.tr', color: '#9F1239', short: 'AKT' },
        { title: 'Biyoloji',           href: 'https://biology.hacettepe.edu.tr',  color: '#166534', short: 'BİY' },
        { title: 'Kimya',              href: 'https://chem.hacettepe.edu.tr',     color: '#1E3A8A', short: 'KİM' },
        { title: 'Matematik',          href: 'https://mat.hacettepe.edu.tr',      color: '#4C1D95', short: 'MAT' },
        { title: 'İstatistik',         href: 'https://stat.hacettepe.edu.tr',     color: '#92400E', short: 'İST' },
    ], []);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="fixed inset-0 z-[1090]"
                        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' }}
                        onClick={close}
                    />

                    {/* ── Sağdan kayan panel ── */}
                    <motion.div
                        key="panel"
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'tween', duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="fixed top-0 right-0 bottom-0 z-[1100] flex flex-col bg-white overflow-hidden"
                        style={{ width: 'min(480px, 100vw)', boxShadow: '-6px 0 40px rgba(0,0,0,0.16)' }}
                    >
                        {/* ── Header ── */}
                        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2.5">
                                <img src="https://kkm.hacettepe.edu.tr/wp-content/uploads/2018/07/cropped-logo-hacettepe.png"
                                     alt="Hacettepe" className="h-9" />
                                <div className="leading-tight">
                                    <p style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 500, letterSpacing: '0.04em' }}>Hacettepe Üniversitesi</p>
                                    <p style={{ fontSize: '13px', fontWeight: 800, color: '#7B0D1E', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Fen Fakültesi</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Dil */}
                                <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                                    {['tr', 'en'].map(lang => (
                                        <button key={lang} onClick={() => changeLanguage(lang)}
                                                className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${
                                                    currentLang === lang ? 'bg-[#7B0D1E] text-white' : 'text-gray-400 hover:text-gray-700'
                                                }`}>{lang}</button>
                                    ))}
                                </div>
                                <button onClick={close}
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-[#7B0D1E] text-gray-500 hover:text-white transition-all duration-200">
                                    <XIcon size={16} />
                                </button>
                            </div>
                        </div>

                        {/* ── Arama ── */}
                        <div className="flex-shrink-0 px-5 py-3 border-b border-gray-50">
                            <SiteSearch placeholder="Sitede ara…" />
                        </div>

                        {/* ── Kaydırılabilir gövde ── */}
                        <div className="flex-1 overflow-y-auto">

                            {/* ── İkon grid — her satırın hemen altında alt menü açılır ── */}
                            <div className="px-5 pt-5 pb-2">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">Menü</span>
                                    <div className="flex-1 h-px bg-gray-200" />
                                </div>

                                {Array.from({ length: Math.ceil(sections.length / 2) }, (_, rowIdx) => {
                                    const rowSections = sections.slice(rowIdx * 2, rowIdx * 2 + 2);
                                    const activeColInRow = rowSections.findIndex((_, colIdx) => rowIdx * 2 + colIdx === activeIdx);
                                    const activeSecInRow = activeColInRow >= 0 ? sections[rowIdx * 2 + activeColInRow] : null;

                                    return (
                                        <div key={rowIdx} className="mb-2">
                                            {/* Satırdaki kartlar */}
                                            <div className="grid grid-cols-2 gap-2">
                                                {rowSections.map((sec, colIdx) => {
                                                    const idx = rowIdx * 2 + colIdx;
                                                    const active = idx === activeIdx;
                                                    const Icon = sec.icon;
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setActiveIdx(active ? null : idx)}
                                                            className="flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left transition-all duration-200"
                                                            style={{
                                                                borderColor: active ? sec.color : '#F3F4F6',
                                                                background: active ? sec.color + '0d' : '#F9FAFB',
                                                                boxShadow: active ? `0 3px 14px ${sec.color}22` : 'none',
                                                            }}
                                                        >
                                                            <div
                                                                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                                                                style={{
                                                                    background: active
                                                                        ? `linear-gradient(135deg, ${sec.color}, ${sec.color}bb)`
                                                                        : sec.color + '1a',
                                                                }}
                                                            >
                                                                <Icon size={20} color={active ? '#fff' : sec.color} />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-[12px] font-bold leading-tight"
                                                                   style={{ color: active ? sec.color : '#374151' }}>
                                                                    {sec.label}
                                                                </p>
                                                                <p className="text-[10px] mt-0.5"
                                                                   style={{ color: active ? sec.color + 'aa' : '#9CA3AF' }}>
                                                                    {sec.links.length} bağlantı
                                                                </p>
                                                            </div>
                                                            <ChevronRight
                                                                size={14}
                                                                className="flex-shrink-0 transition-transform duration-200"
                                                                style={{
                                                                    color: active ? sec.color : '#D1D5DB',
                                                                    transform: active ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                }}
                                                            />
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            {/* Bu satırın alt menüsü */}
                                            <AnimatePresence mode="wait">
                                                {activeSecInRow && (
                                                    <motion.div
                                                        key={activeIdx}
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.16, ease: 'easeOut' }}
                                                        style={{ overflow: 'hidden' }}
                                                    >
                                                        <div
                                                            className="mt-2 rounded-2xl overflow-hidden"
                                                            style={{
                                                                border: `2px solid ${activeSecInRow.color}30`,
                                                                background: `${activeSecInRow.color}07`,
                                                            }}
                                                        >
                                                            {/* Başlık */}
                                                            <div
                                                                className="flex items-center gap-2.5 px-4 py-3"
                                                                style={{
                                                                    background: `${activeSecInRow.color}14`,
                                                                    borderBottom: `1.5px solid ${activeSecInRow.color}20`,
                                                                }}
                                                            >
                                                                {React.createElement(activeSecInRow.icon, { size: 15, color: activeSecInRow.color })}
                                                                <span
                                                                    className="text-[11.5px] font-black uppercase tracking-wider"
                                                                    style={{ color: activeSecInRow.color }}
                                                                >
                                                                    {activeSecInRow.label}
                                                                </span>
                                                            </div>

                                                            {/* Bağlantılar */}
                                                            {activeSecInRow.links.map((link) => (
                                                                link.external ? (
                                                                    <a
                                                                        key={link.title}
                                                                        href={link.href}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={close}
                                                                        className="flex items-center gap-3 px-4 py-3 border-b border-white/60 last:border-0 hover:bg-white/70 transition-colors group"
                                                                    >
                                                                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                                             style={{ backgroundColor: activeSecInRow.color }} />
                                                                        <span className="text-[13.5px] font-medium text-gray-700 group-hover:text-gray-950 flex-1 leading-snug">
                                                                            {link.title}
                                                                        </span>
                                                                        <ExternalLink size={11} className="text-gray-300 flex-shrink-0 group-hover:text-gray-500 transition-colors" />
                                                                    </a>
                                                                ) : (
                                                                    <Link
                                                                        key={link.title}
                                                                        to={link.href}
                                                                        onClick={close}
                                                                        className="flex items-center gap-3 px-4 py-3 border-b border-white/60 last:border-0 hover:bg-white/70 transition-colors group"
                                                                    >
                                                                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                                             style={{ backgroundColor: activeSecInRow.color }} />
                                                                        <span className="text-[13.5px] font-medium text-gray-700 group-hover:text-gray-950 flex-1 leading-snug">
                                                                            {link.title}
                                                                        </span>
                                                                        <ArrowRight size={11} className="text-gray-300 flex-shrink-0 group-hover:translate-x-0.5 transition-all" style={{ color: 'inherit' }} />
                                                                    </Link>
                                                                )
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* ── Tekil bağlantılar ── */}
                            <div className="px-5 py-3 flex flex-wrap gap-2 border-t border-gray-50 mt-2">
                                {singleLinks.map((link, idx) => (
                                    <Link key={idx} to={link.href} onClick={close}
                                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11.5px] font-semibold text-gray-400 hover:text-[#7B0D1E] hover:bg-red-50 border border-gray-100 hover:border-red-100 transition-all">
                                        <ArrowRight size={10} />
                                        {link.title}
                                    </Link>
                                ))}
                            </div>

                            {/* ── Bölümlerimiz ── */}
                            <div className="px-5 py-4 border-t border-gray-100">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">Bölümlerimiz</span>
                                    <div className="flex-1 h-px bg-gray-200" />
                                </div>
                                <div className="grid grid-cols-5 gap-2">
                                    {deptCards.map(dept => (
                                        <a key={dept.title} href={dept.href} target="_blank" rel="noopener noreferrer" onClick={close}
                                           className="flex flex-col items-center gap-1.5 group">
                                            <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white text-[10px] font-black shadow-sm group-hover:shadow-md transition-shadow"
                                                 style={{ backgroundColor: dept.color }}>
                                                {dept.short}
                                            </div>
                                            <span className="text-[9px] text-gray-400 text-center leading-tight">{dept.title}</span>
                                        </a>
                                    ))}
                                </div>
                            </div>

                            {/* ── Son Duyuru ── */}
                            {latestAnnouncement && (
                                <div className="px-5 py-4 border-t border-gray-100">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">Son Duyuru</span>
                                        <div className="flex-1 h-px bg-gray-200" />
                                    </div>
                                    {latestAnnouncement.link?.startsWith('http') ? (
                                        <a href={latestAnnouncement.link} target="_blank" rel="noopener noreferrer" onClick={close}
                                           className="block p-3.5 rounded-xl bg-red-50 border border-red-100 hover:border-[#7B0D1E]/30 hover:shadow-sm transition-all group">
                                            <span className="block w-1.5 h-1.5 rounded-full bg-red-500 mb-2 animate-pulse" />
                                            <p className="text-[12px] text-gray-700 font-semibold leading-snug group-hover:text-[#7B0D1E] line-clamp-2">{latestAnnouncement.title}</p>
                                            <p className="text-[10px] text-gray-400 mt-1.5">{new Date(latestAnnouncement.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}</p>
                                        </a>
                                    ) : (
                                        <Link to={prefix + "/duyurular"} onClick={close}
                                              className="block p-3.5 rounded-xl bg-red-50 border border-red-100 hover:border-[#7B0D1E]/30 hover:shadow-sm transition-all group">
                                            <span className="block w-1.5 h-1.5 rounded-full bg-red-500 mb-2 animate-pulse" />
                                            <p className="text-[12px] text-gray-700 font-semibold leading-snug group-hover:text-[#7B0D1E] line-clamp-2">{latestAnnouncement.title}</p>
                                            <p className="text-[10px] text-gray-400 mt-1.5">{new Date(latestAnnouncement.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}</p>
                                        </Link>
                                    )}
                                    <Link to={prefix + "/duyurular"} onClick={close}
                                          className="flex items-center gap-1 mt-2 text-[11px] text-[#7B0D1E] hover:text-[#9B1A2E] font-bold transition-colors">
                                        Tüm duyurular <ArrowRight size={10} />
                                    </Link>
                                </div>
                            )}

                            {/* ── İstatistikler ── */}
                            <div className="px-5 py-4 border-t border-gray-100">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">Fen Fakültesi</span>
                                    <div className="flex-1 h-px bg-gray-200" />
                                </div>
                                <div className="rounded-xl border border-gray-100 divide-y divide-gray-50 overflow-hidden">
                                    {[
                                        { icon: Layers,        label: 'Bölüm',          value: '5' },
                                        { icon: GraduationCap, label: 'Lisans Programı', value: '5' },
                                        ...(!yayinHata ? [{ icon: BookOpen, label: 'Yayın (2026)',
                                          value: yayinSayisi !== null
                                            ? yayinSayisi.toLocaleString('tr-TR')
                                            : <span className="text-gray-300 text-[11px]">yükleniyor…</span> }] : []),
                                    ].map((stat, i) => {
                                        const Icon = stat.icon;
                                        return (
                                            <div key={i} className="flex items-center gap-3 px-4 py-2.5 bg-white">
                                                <Icon size={12} className="text-gray-300 flex-shrink-0" />
                                                <span className="text-[11.5px] text-gray-500 flex-1">{stat.label}</span>
                                                <span className="text-[13px] font-black text-gray-800">{stat.value}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* bottom padding */}
                            <div className="h-6" />
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

/* ─── ANA NAVBAR ─── */
const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isPromotionsOpen, setIsPromotionsOpen] = useState(false);
    const [isStudentMenuOpen, setIsStudentMenuOpen] = useState(false);
    const [isResearchMenuOpen, setIsResearchMenuOpen] = useState(false);
    const [isProgramsMenuOpen, setIsProgramsMenuOpen] = useState(false);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const navLinks = getNavLinks(t, i18n.language);
    const departmentPromotions = getDepartmentPromotions(t, i18n.language);

    const studentMenu = navLinks.find(link => link.title === t('nav.student'));
    const researchMenu = navLinks.find(link => link.title === t('nav.research'));
    const programsMenu = navLinks.find(link => link.title === t('nav.programs'));

    const closeAllMenus = () => {
        setIsPromotionsOpen(false);
        setIsStudentMenuOpen(false);
        setIsResearchMenuOpen(false);
        setIsProgramsMenuOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.header-dropdown-menu') && !e.target.closest('.department-promotions-menu')) {
                closeAllMenus();
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const changeLanguage = (lang) => {
        let newPath = location.pathname;
        const urlMappings = { '/ogrenci/uluslararasi-ogrenciler': '/en/student/international-students' };
        const reverseUrlMappings = { '/en/student/international-students': '/ogrenci/uluslararasi-ogrenciler' };
        if (lang === 'en') {
            if (urlMappings[newPath]) newPath = urlMappings[newPath];
            else if (!newPath.startsWith('/en/') && newPath !== '/en') newPath = newPath === '/' ? '/en' : '/en' + newPath;
        } else {
            if (reverseUrlMappings[newPath]) newPath = reverseUrlMappings[newPath];
            else if (newPath.startsWith('/en/') || newPath === '/en') newPath = newPath.replace(/^\/en/, '') || '/';
        }
        navigate(newPath);
    };

    const currentLang = i18n.language;

    return (
        <>
            <nav className="main-navbar-transparent">
                <div className="navbar-container-transparent">
                    {/* Sol: Logo */}
                    <div className="navbar-left">
                        <Link to="/" className="navbar-logo-rebranded">
                            <img src="https://kkm.hacettepe.edu.tr/wp-content/uploads/2018/07/cropped-logo-hacettepe.png" alt="Hacettepe Üniversitesi Logosu" className="main-logo"/>
                            <div className="logo-text-rebranded">
                                <h1>{t('navbar.universityName')}</h1>
                                <h2>{t('navbar.facultyNameUpper')}</h2>
                            </div>
                            <img src="https://iysportal.hacettepe.edu.tr/templates/template3/images/yokak5.svg" alt="YÖKAK Logosu" className="yokak-logo"/>
                        </Link>
                        <div className="navbar-menus">
                            {/* Öğrenci */}
                            {studentMenu?.sublinks && (
                                <div className="header-dropdown-menu">
                                    <button className="header-dropdown-toggle student-toggle" onClick={(e) => {
                                        e.stopPropagation();
                                        setIsStudentMenuOpen(!isStudentMenuOpen);
                                        setIsResearchMenuOpen(false); setIsPromotionsOpen(false); setIsProgramsMenuOpen(false);
                                    }}>
                                        <GraduationCap size={16} />
                                        <span className="menu-text">{studentMenu.title}</span>
                                        <ChevronDown size={16} className={`transition-transform ${isStudentMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {isStudentMenuOpen && (
                                            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="header-dropdown-content">
                                                {studentMenu.sublinks.map((item, idx) => item.external ? (
                                                    <a key={idx} href={item.href} target="_blank" rel="noopener noreferrer" className="dropdown-item" onClick={() => { setIsStudentMenuOpen(false); trackExternalClick(`Öğrenci - ${item.title}`, item.href); }}>{item.title}</a>
                                                ) : (
                                                    <Link key={idx} to={item.href} className="dropdown-item" onClick={() => { setIsStudentMenuOpen(false); trackNavigation(`Öğrenci - ${item.title}`, item.href); }}>{item.title}</Link>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Araştırma */}
                            {researchMenu?.sublinks && (
                                <div className="header-dropdown-menu">
                                    <button className="header-dropdown-toggle research-toggle" onClick={(e) => {
                                        e.stopPropagation();
                                        setIsResearchMenuOpen(!isResearchMenuOpen);
                                        setIsStudentMenuOpen(false); setIsPromotionsOpen(false); setIsProgramsMenuOpen(false);
                                    }}>
                                        <BookOpen size={16} />
                                        <span className="menu-text">{researchMenu.title}</span>
                                        <ChevronDown size={16} className={`transition-transform ${isResearchMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {isResearchMenuOpen && (
                                            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="header-dropdown-content">
                                                {researchMenu.sublinks.map((item, idx) => item.external ? (
                                                    <a key={idx} href={item.href} target="_blank" rel="noopener noreferrer" className="dropdown-item" onClick={() => { setIsResearchMenuOpen(false); trackExternalClick(`Araştırma - ${item.title}`, item.href); }}>{item.title}</a>
                                                ) : (
                                                    <Link key={idx} to={item.href} className="dropdown-item" onClick={() => { setIsResearchMenuOpen(false); trackNavigation(`Araştırma - ${item.title}`, item.href); }}>{item.title}</Link>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Programlar */}
                            {programsMenu?.sublinks && (
                                <div className="header-dropdown-menu">
                                    <button className="header-dropdown-toggle programs-toggle" onClick={(e) => {
                                        e.stopPropagation();
                                        setIsProgramsMenuOpen(!isProgramsMenuOpen);
                                        setIsStudentMenuOpen(false); setIsResearchMenuOpen(false); setIsPromotionsOpen(false);
                                    }}>
                                        <BookOpen size={16} />
                                        <span className="menu-text">{t('nav.programs')}</span>
                                        <ChevronDown size={16} className={`transition-transform ${isProgramsMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {isProgramsMenuOpen && (
                                            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="header-dropdown-content">
                                                {programsMenu.sublinks.map((item, idx) => item.external ? (
                                                    <a key={idx} href={item.href} target="_blank" rel="noopener noreferrer" className="dropdown-item" onClick={() => { setIsProgramsMenuOpen(false); trackExternalClick(`Programlar - ${item.title}`, item.href); }}>{item.title}</a>
                                                ) : (
                                                    <Link key={idx} to={item.href} className="dropdown-item" onClick={() => { setIsProgramsMenuOpen(false); trackNavigation(`Programlar - ${item.title}`, item.href); }}>{item.title}</Link>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Tanıtım */}
                            <div className="department-promotions-menu">
                                <button className="department-promotions-toggle" onClick={(e) => {
                                    e.stopPropagation();
                                    setIsPromotionsOpen(!isPromotionsOpen);
                                    setIsStudentMenuOpen(false); setIsResearchMenuOpen(false); setIsProgramsMenuOpen(false);
                                }}>
                                    <GraduationCap size={16} />
                                    {t('navbar.departmentPromotions')} <ChevronDown size={16} className={`transition-transform ${isPromotionsOpen ? 'rotate-180' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {isPromotionsOpen && (
                                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="department-promotions-dropdown">
                                            {departmentPromotions.map((item, idx) => {
                                                if (item.isDivider) return <div key={idx} className="dropdown-divider" />;
                                                if (item.isHeader) return <div key={idx} className="dropdown-header"><Building2 size={14} className="mr-2" />{item.title}</div>;
                                                const IconComponent = { Youtube, FileText, Building2 }[item.icon];
                                                return item.external ? (
                                                    <a key={idx} href={item.href} target="_blank" rel="noopener noreferrer" className="dropdown-item" onClick={() => trackExternalClick(`Bölüm Tanıtımı - ${item.title}`, item.href)}>
                                                        {IconComponent && <IconComponent size={16} className="mr-2" />}{item.title}
                                                    </a>
                                                ) : (
                                                    <Link key={idx} to={item.href} className="dropdown-item" onClick={() => { setIsPromotionsOpen(false); trackNavigation(`Sanal Tur - ${item.title}`, item.href); }}>
                                                        {IconComponent && <IconComponent size={16} className="mr-2" />}{item.title}
                                                    </Link>
                                                );
                                            })}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <Link to="/duyurular" className="announcements-btn" onClick={() => trackButtonClick('Duyurular', 'header')}>
                                <Bell size={16} />
                                <span>{t('navbar.announcements')}</span>
                            </Link>
                        </div>
                    </div>

                    {/* Sağ: Dil + Menü */}
                    <div className="navbar-right">
                        <button onClick={() => changeLanguage(currentLang === 'tr' ? 'en' : 'tr')}
                                className="language-switcher-btn" title={currentLang === 'tr' ? 'Switch to English' : "Türkçe'ye Geç"}>
                            <Globe size={16} />
                            <span>{currentLang === 'tr' ? 'EN' : 'TR'}</span>
                        </button>
                        <button className="menu-toggle-btn-transparent" onClick={() => setIsOpen(true)}>
                            <span>{currentLang === 'en' ? 'Menu' : 'Menü'}</span>
                            <MenuIcon />
                        </button>
                    </div>
                </div>
            </nav>

            <MegaMenu isOpen={isOpen} setIsOpen={setIsOpen} changeLanguage={changeLanguage} currentLang={currentLang} />
        </>
    );
};

export default Navbar;
